import React, { useEffect, useState, useCallback } from 'react'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { IconButton, Typography, Grid } from  '@mui/material'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { MessageBusType } from 'smartstream-widgets/types/sharedModule/core'
import GSSVG from '../GSSVG'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  classes: {
    [K in string]: string
  },
  clickHandler: ((nextPage: string) => void),
  itemsPerPage: string,
  page: string,
  totalResults: number,
  translations: {
    [K in string]: ((
      arg0: {
        [K in string]: string | number
      }
    ) => string)
  },
  fullPage: boolean,
  messageBus: MessageBusType,
  BASE_ASSET_PATH: string
}

const styles = (theme: Theme) => createStyles({
  arrow: {
    width: 'auto'
  },
  arrowButton: {
    height: 24,
    padding: 0,
    width: 24,
    '&:disabled': {
      '& $leftArrow:after': {
        borderRight: `8px solid ${theme.palette.action.disabled}`
      },
      '& $rightArrow:after': {
        borderLeft: `8px solid ${theme.palette.action.disabled}`
      }
    }
  },
  leftArrow: {
    '&:after': {
      border: '8px solid transparent',
      borderRight: `8px solid ${theme.palette.primary.main}`,
      content: '""',
      transform: 'translateX(-5px)'
    }
  },
  leftButton: {
    marginRight: 24
  },
  paginationLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginRight: 16
  },
  rightArrow: {
    '&:after': {
      border: '8px solid transparent',
      borderLeft: `8px solid ${theme.palette.primary.main}`,
      content: '""',
      transform: 'translateX(5px)'
    }
  },
  root: {}
})

export function FooterPagination(props: Props) {
  const {
    classes,
    clickHandler,
    page = '1',
    itemsPerPage = '1',
    totalResults = 1,
    translations,
    fullPage,
    messageBus,
    BASE_ASSET_PATH
  } = props

  const [currentPage, setCurrentPage] = useState(Number(page))
  const updatePageByMsg = useCallback((msg: { body: number }) => {
    const newPage = msg && msg.body
    newPage && setCurrentPage(Number(newPage))
  }, [])

  useEffect(() => {
    messageBus && messageBus.addEventListener('pagination:change', updatePageByMsg)
    return () => {
      messageBus && messageBus.removeEventListener('pagination:change', updatePageByMsg)
    }
  }, [])

  useEffect(() => {
    setCurrentPage(Number(page))
  }, [page])

  const nItemsPerPage = Number(itemsPerPage)
  const shouldRender = nItemsPerPage < totalResults
  const showFrom = ((currentPage - 1) * nItemsPerPage) + 1
  let showTo = currentPage * nItemsPerPage
  if (showTo > totalResults) {
    showTo = totalResults
  }

  const bindings = { showFrom, showTo, totalResults }
  const arrowHandler = (dir) => {
    const newPage = dir === 'next' ? (currentPage + 1) : (currentPage - 1)
    setCurrentPage(newPage)
    messageBus && messageBus.dispatchEvent('pagination:change', newPage)
    clickHandler && clickHandler(newPage.toString())
  }

  if (!shouldRender) return null
  return (
    <Grid container alignItems={'center'} justifyContent={fullPage ? 'space-between' : 'center'} classes={{ root: classes.root }} data-testid='footer-pagination'>
      {fullPage && <IconButton
        classes={{ root: `${classes.arrowButton} ${classes.arrow}` }}
        className={classes.leftButton}
        data-ht-category='pagination'
        data-ht-action={`page change to ${currentPage - 1}`}
        data-ht-label='prev'
        disabled={showFrom === 1}
        onClick={() => arrowHandler('back')}
      >
        <GSSVG
          viewBox='0 0 8 12'
          xlinkHref={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-left`} />
      </IconButton>}
      <Typography className={classes.paginationLabel}>
        {translations.paginationLabel(bindings)}
      </Typography>
      {!fullPage && <IconButton
        classes={{ root: `${classes.arrowButton} ${classes.arrow}` }}
        className={classes.leftButton}
        data-ht-category='pagination'
        data-ht-action={`page change to ${currentPage - 1}`}
        data-ht-label='prev'
        disabled={showFrom === 1}
        onClick={() => arrowHandler('back')}
      >
        <GSSVG
          viewBox='0 0 8 12'
          xlinkHref={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-left`} />
      </IconButton>}
      <IconButton
        classes={{ root: `${classes.arrowButton} ${classes.arrow}` }}
        data-ht-category='pagination'
        data-ht-action={`page change to ${currentPage + 1}`}
        data-ht-label='next'
        disabled={showTo === totalResults}
        onClick={() => arrowHandler('next')}
      >
        <GSSVG
          viewBox='0 0 8 12'
          xlinkHref={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-right`} />
      </IconButton>
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'FooterPagination', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      paginationLabel: (bindings: { [key in string]: string | number }) => t('footerPagination.pagination.translation', {
        defaultValue: '{{showFrom}}-{{showTo}} of {{totalResults}} games',
        context: 'Text to display pagination information in FooterPagination',
        replace: bindings
      })
    }
  })
)(FooterPagination)
